import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import AdminLayout from '@/pages/admin/components/AdminLayout';
import ProductFormModal from './components/ProductFormModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';

interface Product {
  id: string;
  name: string;
  product_code: string | null;
  description: string;
  long_description: string | null;
  price: number;
  original_price: number | null;
  category: string;
  image: string;
  images: string[] | null;
  tags: string[] | null;
  is_featured: boolean;
  is_visible: boolean;
  moq?: number;
  sort_order: number;
  specs: Record<string, string> | null;
  highlights: unknown[] | null;
  faqs: unknown[] | null;
  is_published?: boolean;
  created_at: string;
}

const CATEGORY_COLORS: Record<string, string> = {
  '瓦楞纸': 'bg-amber-50 text-amber-700',
  '剑麻': 'bg-green-50 text-green-700',
  '综合': 'bg-[#f0ebe4] text-[#C8714A]',
};

const TAG_STYLES: Record<string, string> = {
  '热销中': 'bg-red-50 text-red-600',
  '即将上市': 'bg-blue-50 text-blue-600',
  '已下架': 'bg-gray-100 text-gray-500',
};

export default function AdminProductsPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('全部');

  const [formModal, setFormModal] = useState<{ open: boolean; product: Product | null }>({ open: false, product: null });
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; product: Product | null }>({ open: false, product: null });
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState('');
  const [batchTranslating, setBatchTranslating] = useState(false);
  const [batchResult, setBatchResult] = useState<{ total: number; translated: number } | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: false });

    if (!error && data) setProducts(data as Product[]);
    setLoading(false);
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleDelete = async () => {
    if (!deleteModal.product) return;
    setDeleting(true);
    const { error } = await supabase.from('products').delete().eq('id', deleteModal.product.id);
    setDeleting(false);
    setDeleteModal({ open: false, product: null });
    if (!error) {
      showToast('商品已删除');
      fetchProducts();
    } else {
      showToast('删除失败，请重试');
    }
  };

  const handleSaved = () => {
    setFormModal({ open: false, product: null });
    showToast(formModal.product ? '商品已更新' : '商品已创建');
    fetchProducts();
  };

  const handleBatchTranslate = async () => {
    setBatchTranslating(true);
    setBatchResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('batch-translate', {
        body: {},
      });
      if (error) throw error;
      const result = data as { total: number; translated: number };
      setBatchResult(result);
      showToast(`翻译完成！${result.total} 件商品，补充了 ${result.translated} 个语言版本`);
      fetchProducts();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '批量翻译失败';
      showToast(msg);
    } finally {
      setBatchTranslating(false);
    }
  };

  const toggleVisibility = async (product: Product) => {
    const newVal = !product.is_visible;
    const { error } = await supabase
      .from('products')
      .update({ is_visible: newVal })
      .eq('id', product.id);
    if (!error) {
      showToast(newVal ? '商品已显示' : '商品已隐藏');
      fetchProducts();
    } else {
      showToast('操作失败，请重试');
    }
  };

  const toggleFeatured = async (product: Product) => {
    const newVal = !product.is_featured;
    const { error } = await supabase
      .from('products')
      .update({ is_featured: newVal })
      .eq('id', product.id);
    if (!error) {
      showToast(newVal ? '已在首页推荐' : '已取消首页推荐');
      fetchProducts();
    } else {
      showToast('操作失败，请重试');
    }
  };

  const filtered = products.filter((p) => {
    const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCategory === '全部' || p.category === filterCategory;
    return matchSearch && matchCat;
  });

  const categories = ['全部', ...Array.from(new Set(products.map((p) => p.category).filter(Boolean)))];

  return (
    <AdminLayout title="商品管理">
      {/* Toast */}
      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-[#1a1a1a] text-white text-sm px-5 py-2.5 rounded-full shadow-lg animate-fade-in">
          {toast}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        {/* Search */}
        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#aaa]">
            <i className="ri-search-line text-sm"></i>
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜索商品名称..."
            className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-[#e8e4de] bg-white text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C8714A] transition-colors"
          />
        </div>

        {/* Category filter */}
        <div className="relative">
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="appearance-none px-4 py-2.5 rounded-lg border border-[#e8e4de] bg-white text-sm text-[#1a1a1a] focus:outline-none focus:border-[#C8714A] transition-colors cursor-pointer pr-10"
          >
            {categories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
            <i className="ri-arrow-down-s-line text-[#999]"></i>
          </div>
        </div>

        {/* Add button */}
        <button
          onClick={() => setFormModal({ open: true, product: null })}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#C8714A] text-white text-sm font-semibold hover:bg-[#b5603b] transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
        >
          <i className="ri-add-line"></i>
          新增商品
        </button>

        {/* Batch translate */}
        <button
          onClick={handleBatchTranslate}
          disabled={batchTranslating}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#C8714A] text-[#C8714A] text-sm font-medium hover:bg-[#C8714A]/5 transition-colors cursor-pointer whitespace-nowrap flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          title="为所有缺少翻译的商品自动翻译全部 6 种语言"
        >
          {batchTranslating ? (
            <>
              <div className="w-4 h-4 border-2 border-[#C8714A] border-t-transparent rounded-full animate-spin"></div>
              翻译中...
            </>
          ) : (
            <>
              <i className="ri-translate-2"></i>
              批量翻译缺失
            </>
          )}
        </button>
      </div>

      {/* Stats */}
      <div className="text-xs text-[#aaa] mb-3">
        共 <span className="text-[#C8714A] font-medium">{filtered.length}</span> 件商品
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-[#ede9e3] overflow-hidden">
        {loading ? (
          <div className="flex flex-col gap-3 p-6">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-14 bg-[#f7f5f2] rounded-lg animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-[#aaa]">
            <div className="w-12 h-12 flex items-center justify-center bg-[#f7f5f2] rounded-full">
              <i className="ri-box-3-line text-xl"></i>
            </div>
            <p className="text-sm">{search ? '没有找到匹配的商品' : '暂无商品，点击「新增商品」开始添加'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#ede9e3] bg-[#faf9f7]">
                  <th className="text-left text-xs font-medium text-[#aaa] px-4 py-3">商品</th>
                  <th className="text-left text-xs font-medium text-[#aaa] px-3 py-3">产品代码</th>
                  <th className="text-left text-xs font-medium text-[#aaa] px-4 py-3 hidden sm:table-cell">分类</th>
                  <th className="text-left text-xs font-medium text-[#aaa] px-4 py-3">价格</th>
                  <th className="text-left text-xs font-medium text-[#aaa] px-4 py-3 hidden md:table-cell">标签</th>
                  <th className="text-center text-xs font-medium text-[#aaa] px-3 py-3">首页推荐</th>
                  <th className="text-center text-xs font-medium text-[#aaa] px-3 py-3">商品显示</th>
                  <th className="text-right text-xs font-medium text-[#aaa] px-4 py-3">操作</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((product, idx) => (
                  <tr
                    key={product.id}
                    className={`border-b border-[#f0ece6] last:border-0 hover:bg-[#faf9f7] transition-colors ${idx % 2 === 0 ? '' : 'bg-[#fdfcfb]'}`}
                  >
                    {/* Product info */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-[#f7f5f2] flex-shrink-0">
                          {product.image ? (
                            <img src={product.image} alt={product.name} className="w-full h-full object-cover object-center" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <i className="ri-image-line text-[#ccc] text-sm"></i>
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-[#1a1a1a] truncate max-w-[160px]">{product.name}</div>
                        </div>
                      </div>
                    </td>

                    {/* Product Code */}
                    <td className="px-3 py-3">
                      {product.product_code ? (
                        <span className="text-xs text-[#888] font-mono whitespace-nowrap">{product.product_code}</span>
                      ) : (
                        <span className="text-xs text-[#ccc]">—</span>
                      )}
                    </td>

                    {/* Category */}
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${CATEGORY_COLORS[product.category] ?? 'bg-[#f0ece6] text-[#888]'}`}>
                        {product.category}
                      </span>
                    </td>

                    {/* Price */}
                    <td className="px-4 py-3">
                      <div className="text-sm font-semibold text-[#C8714A]">${product.price}</div>
                      {product.original_price && (
                        <div className="text-xs text-[#bbb] line-through">${product.original_price}</div>
                      )}
                    </td>

                    {/* Tags */}
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {(product.tags ?? []).slice(0, 2).map((tag) => (
                          <span key={tag} className={`px-1.5 py-0.5 text-xs rounded font-medium ${TAG_STYLES[tag] ?? 'bg-[#f0ece6] text-[#888]'}`}>
                            {tag}
                          </span>
                        ))}
                        {(!product.tags || product.tags.length === 0) && (
                          <span className="text-xs text-[#ccc]">—</span>
                        )}
                      </div>
                    </td>

                    {/* Homepage Featured Toggle */}
                    <td className="px-3 py-3 text-center">
                      <button
                        onClick={() => toggleFeatured(product)}
                        className={`w-9 h-5 rounded-full transition-colors relative cursor-pointer ${product.is_featured ? 'bg-[#C8714A]' : 'bg-[#ddd]'}`}
                      >
                        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${product.is_featured ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </button>
                    </td>

                    {/* Visibility Toggle */}
                    <td className="px-3 py-3 text-center">
                      <button
                        onClick={() => toggleVisibility(product)}
                        className={`w-9 h-5 rounded-full transition-colors relative cursor-pointer ${product.is_visible ? 'bg-[#C8714A]' : 'bg-[#ddd]'}`}
                      >
                        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${product.is_visible ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </button>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setFormModal({ open: true, product })}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-[#888] hover:bg-[#f0ece6] hover:text-[#C8714A] transition-colors cursor-pointer"
                          title="编辑"
                        >
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => setDeleteModal({ open: true, product })}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-[#888] hover:bg-red-50 hover:text-red-500 transition-colors cursor-pointer"
                          title="删除"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {formModal.open && (
        <ProductFormModal
          product={formModal.product}
          onClose={() => setFormModal({ open: false, product: null })}
          onSaved={handleSaved}
        />
      )}
      {deleteModal.open && deleteModal.product && (
        <DeleteConfirmModal
          productName={deleteModal.product.name}
          onConfirm={handleDelete}
          onCancel={() => setDeleteModal({ open: false, product: null })}
          deleting={deleting}
        />
      )}
    </AdminLayout>
  );
}